import type { CuratorPerson, GroupConflict } from '@/api/curatorGroups';

import { apiErrorCode } from './apiErrorCode';
import { humanizeError } from './errors';

/**
 * Отказы кураторских ручек групп и подтипов (D40, договор
 * `docs/CONTRACTS/curator-groups.md` §7).
 *
 * ⚠️ РАЗЛИЧАТЬ ПРИЧИНЫ ТОЛЬКО ПО `code`. Текст `message` — для человека и
 * меняется; `humanizeError` для 409 говорит «такая запись уже существует», что
 * для «это чужая группа» просто неправда.
 *
 * ⚠️ ТЕЛО ОТКАЗА ЛЕЖИТ В `raw`. Клиент API (`api/client.ts`, `normaliseError`)
 * отдаёт `{status, message, raw}` — поля `response` у такой ошибки нет. На этом
 * уже обжигались 10.09.2026, см. шапку `apiErrorCode.ts`.
 */

const CODE_KEYS: Record<string, string> = {
  unknown_subtype: 'adminSubtypes.errUnknownSubtype',
  invalid_name: 'curatorGroups.errInvalidName',
  not_your_group: 'curatorGroups.errNotYourGroup',
  marathon_not_found: 'curatorGroups.errMarathonNotFound',
  group_not_found: 'curatorGroups.errGroupNotFound',
  student_not_found: 'curatorGroups.errStudentNotFound',
  marathon_mismatch: 'curatorGroups.errMarathonMismatch',
  enrollment_inactive: 'curatorGroups.errEnrollmentInactive',
  not_curator: 'adminSubtypes.errNotCurator',
  user_not_found: 'adminSubtypes.errUserNotFound',
  // `student_in_other_group` здесь НЕТ намеренно: у него не строка, а окно со
  // списком «ученик — группа — куратор» и повтор вызова с confirm.
};

export function curatorGroupsErrorKey(err: unknown): string | null {
  const code = apiErrorCode(err);
  if (code) return CODE_KEYS[code] ?? null;
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return status === 403 ? 'curatorGroups.errForbidden' : null;
}

/** Текст отказа: свой по коду, иначе общий. */
export function curatorGroupsErrorText(err: unknown, t: (key: string) => string): string {
  const key = curatorGroupsErrorKey(err);
  return key ? t(key) : humanizeError(err);
}

export function isTakeoverConflict(err: unknown): boolean {
  return apiErrorCode(err) === 'student_in_other_group';
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/** Тело отказа — из клиента API (`raw`) либо из сырой ошибки axios. */
function errorBody(err: unknown): Record<string, unknown> | undefined {
  if (!isObject(err)) return undefined;
  if (isObject(err.raw)) return err.raw;
  const response = err.response;
  if (isObject(response) && isObject(response.data)) return response.data;
  return undefined;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

function people(v: unknown): CuratorPerson[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter(isObject)
    .map((c) => ({ id: str(c.id) ?? '', name: str(c.name) ?? '' }))
    .filter((c) => c.id || c.name);
}

/**
 * Чужие ученики из отказа 409. Записи без `enrollment_id` или без имени
 * отбрасываем: строка «— в группе —» в окне подтверждения хуже, чем её
 * отсутствие, а решение по ней куратор всё равно принять не может.
 */
export function takeoverConflicts(err: unknown): GroupConflict[] {
  if (!isTakeoverConflict(err)) return [];
  const raw = errorBody(err)?.conflicts;
  if (!Array.isArray(raw)) return [];
  return raw.filter(isObject).flatMap((c) => {
    const enrollmentId = str(c.enrollment_id);
    const name = str(c.student_name);
    if (!enrollmentId || !name) return [];
    return [
      {
        enrollment_id: enrollmentId,
        student_id: str(c.student_id) ?? '',
        student_name: name,
        group_id: str(c.group_id),
        group_name: str(c.group_name),
        curators: people(c.curators),
      },
    ];
  });
}
